import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import { Card, Button, Alert, Typography, Space, Result } from 'antd';
import { SafetyCertificateOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { getRegisterOptions, verifyRegistration } from '../lib/api';

const { Title, Text } = Typography;

export default function RegisterPasskey() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const userId = searchParams.get('userId');
  const email = searchParams.get('email') || '';

  const handleRegister = async () => {
    if (!userId) {
      setError('缺少使用者 ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const options = await getRegisterOptions(userId, email);

      const challengeId = options.challengeId;
      const { challengeId: _, ...webauthnOptions } = options;

      const registrationResponse = await startRegistration(webauthnOptions);

      await verifyRegistration({
        userId: Number(userId),
        challengeId,
        response: registrationResponse,
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      setError(err.message || '註冊 Passkey 失敗');
    } finally {
      setLoading(false);
    }
  };

  if (!userId) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: 24 }}>
        <Card>
          <Alert title="錯誤" description="缺少必要的參數，請聯繫管理員。" type="error" showIcon />
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', padding: 24 }}>
        <Card>
          <Result
            status="success"
            icon={<CheckCircleOutlined />}
            title="註冊成功！"
            subTitle="您的 Passkey 已設定完成，正在導向登入頁面..."
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: 24 }}>
      <Card style={{ width: '100%', maxWidth: 420, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          <div style={{ textAlign: 'center' }}>
            <SafetyCertificateOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
            <Title level={3} style={{ margin: 0 }}>註冊 Passkey</Title>
            <Text type="secondary">為帳號 {email} 設定無密碼登入</Text>
          </div>

          {error && <Alert title={error} type="error" showIcon closable />}

          <Alert
            title="什麼是 Passkey？"
            description={
              <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
                <li>使用指紋、臉部辨識或裝置 PIN 碼登入</li>
                <li>無需記憶密碼，更安全方便</li>
                <li>支援 Windows Hello、Touch ID 等</li>
              </ul>
            }
            type="info"
            showIcon
          />

          <Button
            type="primary"
            size="large"
            block
            icon={<SafetyCertificateOutlined />}
            onClick={handleRegister}
            loading={loading}
          >
            {loading ? '註冊中...' : '註冊 Passkey'}
          </Button>
        </Space>
      </Card>
    </div>
  );
}
